import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { MessageSquare, GitBranch, Sparkles } from 'lucide-react';

export default function HomePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="border-b bg-white/80 backdrop-blur-sm shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl shadow-lg">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">WiseQuest Studio</h1>
              <p className="text-slate-600">Конструкторы для GTA5 RP сервера</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Выберите конструктор
          </h2>
          <p className="text-lg text-slate-600">
            Визуальные редакторы для создания квестов и диалогов
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Quest Builder Card */}
          <Card
            className="group hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 hover:border-primary"
            onClick={() => navigate('/quest-builder')}
          >
            <CardHeader className="space-y-4">
              <div className="flex justify-center">
                <div className="p-6 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl shadow-xl group-hover:scale-110 transition-transform">
                  <GitBranch className="w-16 h-16 text-white" />
                </div>
              </div>
              <div className="text-center">
                <CardTitle className="text-2xl mb-2">Конструктор квестов</CardTitle>
                <CardDescription className="text-base">
                  Создавайте квесты с визуальным редактором стадий
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span>Визуальный граф стадий квеста</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span>Цели и условия выполнения</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span>Награды и переходы</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span>Экспорт в JSON</span>
                </div>
              </div>
              <Button
                className="w-full mt-4 group-hover:bg-primary group-hover:scale-105 transition-all"
                size="lg"
              >
                Открыть конструктор
              </Button>
            </CardContent>
          </Card>

          {/* Dialog Builder Card */}
          <Card
            className="group hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 hover:border-purple-500"
            onClick={() => navigate('/dialog-builder')}
          >
            <CardHeader className="space-y-4">
              <div className="flex justify-center">
                <div className="p-6 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl shadow-xl group-hover:scale-110 transition-transform">
                  <MessageSquare className="w-16 h-16 text-white" />
                </div>
              </div>
              <div className="text-center">
                <CardTitle className="text-2xl mb-2">Конструктор диалогов</CardTitle>
                <CardDescription className="text-base">
                  Создавайте диалоговые деревья с действиями
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  <span>Визуальный граф диалогов</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  <span>Варианты ответов с ветвлениями</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  <span>Действия и события</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  <span>Цепочки экшенов</span>
                </div>
              </div>
              <Button
                className="w-full mt-4 bg-purple-600 hover:bg-purple-700 group-hover:scale-105 transition-all"
                size="lg"
              >
                Открыть конструктор
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Info Section */}
        <div className="mt-16 text-center">
          <Card className="max-w-2xl mx-auto bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
            <CardContent className="pt-6">
              <h3 className="text-xl font-semibold mb-3 text-slate-900">
                Профессиональные инструменты
              </h3>
              <p className="text-slate-600">
                Используйте визуальные редакторы на основе React Flow для создания
                сложных квестов и диалоговых систем. Экспортируйте готовые конфигурации
                в JSON и интегрируйте их в ваш FiveM сервер.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t bg-white/50 backdrop-blur-sm mt-16">
        <div className="max-w-6xl mx-auto px-4 py-6 text-center text-sm text-slate-500">
          <p>WiseQuest Studio • GTA5 RP Quest & Dialog System</p>
        </div>
      </div>
    </div>
  );
}
